import { useState } from 'react';
import { api, ApiRequestError } from '../api';
import { Button, Card, Icon, LanguageToggle } from '../components/ui';
import { useT } from '../i18n';

/**
 * Inicio de sesión.
 *
 * Sólo contraseña: el MVP es de un único administrador (§18). Si la aplicación está
 * detrás de Cloudflare Access, esta pantalla no llega a mostrarse — Access ya autenticó
 * y el Worker acepta esa identidad.
 */
export function Login({ onSuccess }: { onSuccess: () => void }) {
  const { t } = useT();
  const [password, setPassword] = useState('');
  /**
   * Vacío significa «entrar como administrador».
   *
   * Su contraseña vive en los secretos del Worker, no en la tabla, así que su login no lleva
   * correo. Poner un campo obligatorio lo obligaría a inventarse uno para entrar como siempre.
   */
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await api.post('/api/auth/login', {
        password,
        ...(email.trim() ? { email: email.trim() } : {}),
      });
      onSuccess();
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError ? caught.payload.message : t('login.noSePudoConectar'),
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <span
            aria-hidden="true"
            className="accent-gradient mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl text-[var(--color-accent-ink)] shadow-[0_8px_28px_-6px_var(--color-accent)]"
          >
            <Icon name="spark" size={24} />
          </span>
          <h1 className="text-2xl font-semibold tracking-tight">
            Virtual Creator <span className="accent-text">OS</span>
          </h1>
          <p className="mt-1 text-sm text-[var(--color-ink-muted)]">{t('login.panelAdmin')}</p>
        </div>

        <Card accent>
          <form onSubmit={(event) => void submit(event)} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium">
                {t('login.correo')}
                <span className="ml-2 font-normal text-xs text-[var(--color-ink-muted)]">
                  {t('login.correoVacio')}
                </span>
              </label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={t('login.correoPlaceholder')}
                className="mt-1 mb-4 w-full rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-2 text-sm"
              />

              <label htmlFor="password" className="block text-sm font-medium">
                {t('login.contrasena')}
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                autoFocus
                className="mt-2 w-full px-3 py-2 text-sm"
              />
            </div>

            {error && (
              <p role="alert" className="text-sm text-[var(--color-danger)]">
                {error}
              </p>
            )}

            <Button type="submit" disabled={submitting}>
              {submitting ? t('login.verificando') : t('login.entrar')}
            </Button>
          </form>
        </Card>

        <div className="mt-6 flex justify-center">
          <LanguageToggle />
        </div>

        <p className="mt-4 text-center text-xs text-[var(--color-ink-faint)]">
          {t('login.detrasDeAccess')}
        </p>
      </div>
    </div>
  );
}
